import React from 'react';
import type { ASTNode } from './schema';

export interface NodeRendererArgs<T extends ASTNode> {
  node: T;
  nodesToJSX: (args: { nodes: ASTNode[] }) => React.ReactNode[];
}

export type NodeRenderer<T extends ASTNode = ASTNode> = (
  args: NodeRendererArgs<T>,
) => React.ReactNode;

export type NodeRenderers = Record<string, NodeRenderer<never>>;

interface GenerateNodesArgs {
  nodes: ASTNode[];
  path: string;
  renderers: NodeRenderers;
}

export function generateNodes({
  nodes,
  path,
  renderers,
}: GenerateNodesArgs): React.ReactNode[] {
  return nodes.map((node, idx) => {
    const generator = renderers[node.type] as NodeRenderer<ASTNode> | undefined;
    if (!generator) {
      throw new Error(`No renderer found for node type: "${node.type}"`);
    }

    const nodePath = `${path}.${node.type}.${idx}`;

    const reactNode = generator({
      node,
      nodesToJSX: ({ nodes: childNodes }) =>
        generateNodes({ nodes: childNodes, path: nodePath, renderers }),
    });

    if (React.isValidElement(reactNode)) {
      return React.cloneElement(reactNode, { key: nodePath });
    }

    return reactNode;
  });
}
